const source = (data) => {
  return {
    type: "geojson",
    data: data
  };
};

const dashedLineLayer = {
  id: "dashed-line",
  type: "line",
  source: "dashed-line-source",
  layout: {
    "line-join": "round",
    "line-cap": "round"
  },
  paint: {
    "line-color": "orange",
    "line-opacity": 0.75,
    "line-width": 4,
    "line-dasharray": [1, 2]
  }
};

const graphLayer = {
  id: "graph",
  type: "line",
  source: "graph-source",
  layout: {
    "line-cap": "round",
    "line-join": "round",
    "line-miter-limit": 2,
    visibility: "visible"
  },
  paint: {
    "line-color": "#346df1",
    "line-width": {
      stops: [
        [12, 0.2],
        [20, 6]
      ]
    },
    "line-gap-width": 0,
    "line-opacity": 1
  }
};

const readOnlyGraphLayer = {
  id: "readonly-graph",
  type: "line",
  source: "readonly-graph-source",
  layout: {
    "line-cap": "butt",
    "line-join": "bevel",
    "line-miter-limit": 2,
    visibility: "visible"
  },
  paint: {
    "line-color": "#346df1",
    "line-width": {
      stops: [
        [12, 0.2],
        [20, 6]
      ]
    },
    "line-gap-width": 0,
    "line-opacity": 0.75
  }
};

const referenceReadOnlyGraphLayer = {
  id: "reference-readonly-graph",
  type: "line",
  source: "reference-readonly-graph-source",
  layout: {
    "line-cap": "butt",
    "line-join": "bevel",
    "line-miter-limit": 2,
    visibility: "visible"
  },
  paint: {
    "line-color": "#346df1",
    "line-width": {
      stops: [
        [12, 0.2],
        [20, 6]
      ]
    },
    "line-gap-width": 0,
    "line-opacity": 0.5
  }
};

const snappedNodeLayer = {
  id: "snap",
  type: "circle",
  source: "snap-circle-source",
  paint: {
    "circle-color": "#FFFFFF",
    "circle-radius": 12,
    "circle-stroke-width": 6,
    "circle-stroke-color": "#F9A707"
  }
};

const customMarker = ({ markerId = new Date().getTime(), className = "custom-marker" }) => {
  let marker = document.createElement("div");
  marker.className = `${className} ${className}-${markerId}`;
  marker.markerId = markerId;
  return marker;
};

const customTransitionMarker = ({
  markerId = new Date().getTime(),
  className = "custom-marker",
  typeCode = "custom-transition",
  color = null
}) => {
  // If color is provided, create an inline SVG with modified fill color
  if (color) {
    const marker = document.createElement("div");
    marker.className = ` ${className}-${markerId} transition-node ${typeCode}-${markerId} ${typeCode}`;
    marker.markerId = markerId;
    marker.style.width = "24px";
    marker.style.height = "25px";
    marker.style.display = "inline-block";

    // Create wrapper div for content with position relative (for badge positioning)
    const contentWrapper = document.createElement("div");
    contentWrapper.className = "transition-node-content";
    contentWrapper.style.position = "relative";
    contentWrapper.style.width = "100%";
    contentWrapper.style.height = "100%";

    // Create warning badge div (hidden by default, shown via CSS when parent has neighbor-marker-warning class)
    const warningBadge = document.createElement("div");
    warningBadge.className = "transition-node-warning-badge";
    warningBadge.style.display = "none"; // Hidden by default

    // Create inner div for the "!" text
    const warningBadgeInner = document.createElement("div");
    warningBadgeInner.textContent = "!";
    warningBadge.appendChild(warningBadgeInner);

    // Create error badge div (hidden by default, shown via CSS when parent has neighbor-marker-error class)
    const errorBadge = document.createElement("div");
    errorBadge.className = "transition-node-error-badge";
    errorBadge.style.display = "none"; // Hidden by default

    // Create inner div for the "!" text
    const errorBadgeInner = document.createElement("div");
    errorBadgeInner.textContent = "!";
    errorBadge.appendChild(errorBadgeInner);

    // Get SVG source
    let svgSrc;
    try {
      svgSrc = require(`@/assets/custom-icons/${typeCode}.svg`);
    } catch (e) {
      svgSrc = require(`@/assets/custom-icons/custom-transition.svg`);
    }

    // Fetch SVG content and modify fill color
    fetch(svgSrc)
      .then((response) => response.text())
      .then((svgText) => {
        // Replace the fill color in the SVG
        // The SVG has a rect with fill="#00B46C" (green), replace it with the persona color
        // Also handle any other hex color fills on the first rect (background)
        const modifiedSvg = svgText.replace(/<rect[^>]*fill="[^"]*"[^>]*>/, (match) => {
          return match.replace(/fill="[^"]*"/, `fill="${color}"`);
        });
        contentWrapper.innerHTML = modifiedSvg;
        // Add the badges after setting innerHTML (since innerHTML replaces all children)
        contentWrapper.appendChild(warningBadge);
        contentWrapper.appendChild(errorBadge);
      })
      .catch((error) => {
        console.warn("Error loading SVG for custom transition marker:", error);
        // Fallback to img if fetch fails
        const img = document.createElement("img");
        img.src = svgSrc;
        img.style.width = "24px";
        img.style.height = "25px";
        contentWrapper.appendChild(img);
        // Add the badges after appending the img
        contentWrapper.appendChild(warningBadge);
        contentWrapper.appendChild(errorBadge);
      });

    marker.appendChild(contentWrapper);
    return marker;
  }

  // Default behavior: use img element
  let marker = document.createElement("img");
  try {
    marker.src = require(`@/assets/custom-icons/${typeCode}.svg`);
  } catch (e) {
    marker.src = require(`@/assets/custom-icons/custom-transition.svg`);
  }

  marker.className = ` ${className}-${markerId} transition-node ${typeCode}-${markerId} ${typeCode}`;
  marker.markerId = markerId;
  return marker;
};

const createPopupButton = ({ btnText, clickFunc, btnColor }) => {
  let popupBtn = document.createElement("div");
  popupBtn.className = "popup-btn";
  popupBtn.innerText = btnText;
  popupBtn.style.color = btnColor || "black";
  popupBtn.addEventListener("click", () => clickFunc());
  return popupBtn;
};

const createPopupElem = (btnArr) => {
  let popupElem = document.createElement("div");
  popupElem.className = "node-popup";
  return btnArr.reduce((finalPopup, btn) => {
    finalPopup.appendChild(btn);
    return finalPopup;
  }, popupElem);
};

const setIpadHeight = () => {
  document.documentElement.style.setProperty("--ipad-height", `${window.innerHeight}px`);
};

module.exports = {
  source,
  dashedLineLayer,
  graphLayer,
  readOnlyGraphLayer,
  referenceReadOnlyGraphLayer,
  snappedNodeLayer,
  customMarker,
  customTransitionMarker,
  createPopupButton,
  createPopupElem,
  setIpadHeight
};
